import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import MainLayout from '../../components/Layout/MainLayout';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import ApplicationBoard from './ApplicationBoard';
import InterviewBoard from './InterviewBoard';
import CandidateDetailsPanel from './CandidateDetailsPanel';
import ScheduleInterviewModal from './ScheduleInterviewModal';
import styles from './ATSPage.module.css';
import OfferWizardModal from './OfferWizardModal';
import CloseJobModal from './CloseJobModal';
import ConfirmationModal from '../../components/Modals/ConfirmationModal';
import TabBar from '../../components/Tabs/TabBar';
import {
  InterviewItem,
  ATSTab,
  OfferMode,
  InterviewFormData,
} from './types';
import { ATS_CONSTANTS } from './constants';
import { User } from '../../types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { useATSData } from './hooks/useATSData';
import { useATSActions } from './hooks/useATSActions';
import { useBulkActions } from './hooks/useBulkActions';
import ATSHeader from './components/ATSHeader';
import ATSBulkActions from './components/ATSBulkActions';
import { AtsAction } from './utils/atsUtils';
import { categorizeInterviews } from '../../utils/interviewCategorization';

// Extend dayjs
dayjs.extend(utc);
dayjs.extend(timezone);

interface ATSPageProps {
  user: User | null;
  onLogout: () => void;
}

const ATSPage: React.FC<ATSPageProps> = ({ user, onLogout }) => {
  const { jobId } = useParams<{ jobId: string }>();


  // Local UI State
  const [activeTab, setActiveTab] = useState<ATSTab>(ATS_CONSTANTS.TABS.APPLICATIONS);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [candidateToScheduleId, setCandidateToScheduleId] = useState<string | null>(null);
  const [candidatesToBulkScheduleIds, setCandidatesToBulkScheduleIds] = useState<string[] | null>(null);
  const [candidateToEditId, setCandidateToEditId] = useState<string | null>(null);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<Set<string>>(new Set());

  const [isOfferModalOpen, setIsOfferModalOpen] = useState(false);
  const [offerMode, setOfferMode] = useState<OfferMode>(ATS_CONSTANTS.OFFER_MODES.SINGLE);
  const [offerCandidateIds, setOfferCandidateIds] = useState<string[]>([]);

  const [isCloseJobModalOpen, setIsCloseJobModalOpen] = useState(false);
  const [candidateToCancelId, setCandidateToCancelId] = useState<string | null>(null);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const [isBulkRejectConfirmOpen, setIsBulkRejectConfirmOpen] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0);

  const triggerReload = () => setReloadTrigger((prev) => prev + 1);

  // Hooks
  const {
    isLoading,
    columns,
    setColumns,
    jobInfo,
    setJobInfo,
    interviewItems,
  } = useATSData(jobId, reloadTrigger);

  const {
    handleUpdateApplicationStatus,
    handleRejectCandidate,
    handleScheduleInterview,
    handleBulkScheduleInterviewSubmit,
    handleUpdateInterviewStatus,
    handleCancelInterviewClick,
    handleCreateOffer,
    handleCloseJob,
    findCandidate,
  } = useATSActions(jobId, triggerReload, jobInfo, setJobInfo, columns, setColumns, () => setSelectedCandidateId(null));

  const { getBulkActions } = useBulkActions(selectedCandidateIds, findCandidate);

  // Handlers
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId as ATSTab);
    setSelectedCandidateIds(new Set());
  };

  const handleBulkAction = async (action: AtsAction) => {
    switch (action) {
      case 'SCHEDULE_INTERVIEW':
      case 'RESCHEDULE':
        setCandidatesToBulkScheduleIds(Array.from(selectedCandidateIds));
        break;
      case 'REJECT':
        setIsBulkRejectConfirmOpen(true);
        break;
      case 'GENERATE_OFFER':
        setOfferMode(ATS_CONSTANTS.OFFER_MODES.BULK);
        setOfferCandidateIds(Array.from(selectedCandidateIds));
        setIsOfferModalOpen(true);
        break;
      default:
        break;
    }
  };

  const clearSelection = () => {
    setSelectedCandidateIds(new Set());
  };

  const handleScheduleSubmit = async (formData: InterviewFormData) => {
    if (candidateToEditId) {
      // Find candidate to get interview details if needed, but actions mainly need ID
      const found = findCandidate(candidateToEditId);
      if (found) {
        const success = await handleScheduleInterview({
          interview_id: found.candidate.interviewId || '',
          start_at: `${formData.date}T${formData.time}:00+05:30`,
          outlet_id: formData.outlet_id,
          status: ATS_CONSTANTS.STATUS.RESCHEDULED,
        }, true);
        if (success) {
          setCandidateToEditId(null);
          // Auto close panel if open
          setSelectedCandidateId(null);
        }
      }
    } else if (candidateToScheduleId) {
      const found = findCandidate(candidateToScheduleId);
      if (found) {
        const success = await handleScheduleInterview({
          application_id: found.candidate.id,
          start_at: `${formData.date}T${formData.time}:00+05:30`,
          outlet_id: formData.outlet_id,
          status: ATS_CONSTANTS.STATUS.SCHEDULED,
        }, false);
        if (success) {
          setCandidateToScheduleId(null);
          // Auto close panel if open
          setSelectedCandidateId(null);
        }
      }
    }
  };

  const handleBulkScheduleSubmit = async (formData: InterviewFormData) => {
    if (!candidatesToBulkScheduleIds) return;
    const success = await handleBulkScheduleInterviewSubmit({
      application_ids: candidatesToBulkScheduleIds,
      start_at: `${formData.date}T${formData.time}:00+05:30`,
      outlet_id: formData.outlet_id,
      status: ATS_CONSTANTS.STATUS.SCHEDULED,
    });
    if (success) {
      setCandidatesToBulkScheduleIds(null);
      setSelectedCandidateIds(new Set());
    }
  };

  // Derive active items from IDs
  const activeCandidate = selectedCandidateId ? findCandidate(selectedCandidateId)?.candidate || null : null;
  const activeCandidateToSchedule = candidateToScheduleId ? findCandidate(candidateToScheduleId)?.candidate || null : null;
  const activeCandidateToEdit = candidateToEditId ? findCandidate(candidateToEditId)?.candidate || null : null;
  const activeCandidateToCancel = candidateToCancelId ? findCandidate(candidateToCancelId)?.candidate || null : null;
  const activeOfferCandidates = offerCandidateIds.map(id => findCandidate(id)?.candidate).filter(Boolean) as InterviewItem[];

  if (isLoading) {
    return (
      <MainLayout user={user} onLogout={onLogout}>
        <div className={styles.loadingContainer}>
          <LoadingSpinner />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout user={user} onLogout={onLogout}>
      <div className={styles.container} data-testid="ats-page-container">
        <ATSHeader jobInfo={jobInfo} onCloseJob={() => setIsCloseJobModalOpen(true)} />

        <div className={styles.tabsContainer} data-testid="ats-tabs-container">
          <TabBar
            tabs={[
              { id: ATS_CONSTANTS.TABS.APPLICATIONS, label: 'Applications' },
              {
                id: ATS_CONSTANTS.TABS.INTERVIEWS,
                label: 'Interviews',
                badgeCount: categorizeInterviews(interviewItems || []).actionNeeded.length,
              },
            ]}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
        </div>

        {activeTab === ATS_CONSTANTS.TABS.APPLICATIONS ? (
          <ApplicationBoard
            columns={columns}
            selectedCandidates={selectedCandidateIds}
            jobStatus={jobInfo.status}
            onCardClick={(candidate) => setSelectedCandidateId(candidate.id)}
            onToggleSelection={(id) => {
              const newSelected = new Set(selectedCandidateIds);
              if (newSelected.has(id)) {
                newSelected.delete(id);
              } else {
                newSelected.add(id);
              }
              setSelectedCandidateIds(newSelected);
            }}
            onUpdateStatus={(candidate, status) => {
              if (jobInfo.status !== 'Closed' && jobInfo.status !== 'Filled') {
                handleUpdateApplicationStatus(candidate, status);
                setSelectedCandidateId(null); // Auto close
              }
            }}
            onReschedule={(candidate) => {
              if (jobInfo.status !== 'Closed' && jobInfo.status !== 'Filled') {
                setCandidateToEditId(candidate.id);
              }
            }}
            onCancelInterview={(candidate) => {
              if (jobInfo.status !== 'Closed' && jobInfo.status !== 'Filled') {
                setCandidateToCancelId(candidate.id);
                setIsCancelConfirmOpen(true);
              }
            }}
          />
        ) : (
          <InterviewBoard
            interviewItems={interviewItems}
            jobStatus={jobInfo.status}
            onCardClick={(candidate) => {
              // Ensure we have the ID. If it's a raw object, we might need to rely on ID.
              setSelectedCandidateId(candidate.id);
            }}
            onReschedule={(candidate) => {
              if (jobInfo.status !== 'Closed' && jobInfo.status !== 'Filled') {
                setCandidateToEditId(candidate.id);
              }
            }}
            onCancelInterview={(candidate) => {
              if (jobInfo.status !== 'Closed' && jobInfo.status !== 'Filled') {
                setCandidateToCancelId(candidate.id);
                setIsCancelConfirmOpen(true);
              }
            }}
            onUpdateStatus={(candidate, status) => {
              if (jobInfo.status !== 'Closed' && jobInfo.status !== 'Filled') {
                handleUpdateInterviewStatus(candidate, status);
                setSelectedCandidateId(null); // Auto close
              }
            }}
            onGenerateOffer={(candidate) => {
              if (jobInfo.status !== 'Closed' && jobInfo.status !== 'Filled') {
                setOfferMode(ATS_CONSTANTS.OFFER_MODES.SINGLE);
                setOfferCandidateIds([candidate.id]);
                setIsOfferModalOpen(true);
              }
            }}
          />
        )}

        {selectedCandidateIds.size > 0 &&
          jobInfo.status !== 'Closed' &&
          jobInfo.status !== 'Filled' && (
            <ATSBulkActions
            selectedCount={selectedCandidateIds.size}
              actions={getBulkActions()}
              onClearSelection={clearSelection}
              onAction={handleBulkAction}
            />
          )}

        <CandidateDetailsPanel
          candidate={activeCandidate}
          jobStatus={jobInfo.status}
          onClose={() => setSelectedCandidateId(null)}
          onReject={(candidate) => {
            if (jobInfo.status !== 'Closed' && jobInfo.status !== 'Filled') {
              handleRejectCandidate(candidate);
              setSelectedCandidateId(null); // Close the panel
            }
          }}
          onAccept={(candidate) => {
            if (jobInfo.status !== 'Closed' && jobInfo.status !== 'Filled') {
              setOfferMode(ATS_CONSTANTS.OFFER_MODES.SINGLE);
              setOfferCandidateIds([candidate.id]);
              setIsOfferModalOpen(true);
            }
          }}
          onSchedule={(candidate) => {
            if (jobInfo.status !== 'Closed' && jobInfo.status !== 'Filled') {
              setCandidateToScheduleId(candidate.id);
            }
          }}
          onCancelInterview={(candidate) => {
            if (jobInfo.status !== 'Closed' && jobInfo.status !== 'Filled') {
              setCandidateToCancelId(candidate.id);
              setIsCancelConfirmOpen(true);
            }
          }}
          onUpdateStatus={(candidate, status) => {
            if (jobInfo.status !== 'Closed' && jobInfo.status !== 'Filled') {
              handleUpdateInterviewStatus(candidate, status);
              setSelectedCandidateId(null);
            }
          }}
          onReschedule={(candidate) => {
            if (jobInfo.status !== 'Closed' && jobInfo.status !== 'Filled') {
              setCandidateToEditId(candidate.id);
            }
          }}
          onGenerateOffer={(candidate) => {
            if (jobInfo.status !== 'Closed' && jobInfo.status !== 'Filled') {
              setOfferMode(ATS_CONSTANTS.OFFER_MODES.SINGLE);
              setOfferCandidateIds([candidate.id]);
              setIsOfferModalOpen(true);
            }
          }}
        />

        <ScheduleInterviewModal
          isOpen={!!candidateToScheduleId || !!candidatesToBulkScheduleIds || !!candidateToEditId}
          candidate={activeCandidateToSchedule || activeCandidateToEdit}
          candidates={
            candidatesToBulkScheduleIds
              ? Array.from(candidatesToBulkScheduleIds).map((id) => findCandidate(id)!.candidate)
              : null
          }
          isEditMode={!!candidateToEditId}
          onClose={() => {
            setCandidateToScheduleId(null);
            setCandidatesToBulkScheduleIds(null);
            setCandidateToEditId(null);
          }}
          onSchedule={(_, formData) => handleScheduleSubmit(formData)}
          onBulkSchedule={handleBulkScheduleSubmit}
          onEditInterview={(_, formData) => handleScheduleSubmit(formData)}
        />

        <OfferWizardModal
          isOpen={isOfferModalOpen}
          onClose={() => {
            setIsOfferModalOpen(false);
            setOfferCandidateIds([]);
          }}
          candidates={activeOfferCandidates}
          mode={offerMode}
          jobId={jobId}
          outletId={jobInfo?.outlet_id}
          jobTitle={jobInfo?.title}
          onSubmitOffers={async (payload) => {
            const result = await handleCreateOffer(payload);
            
            // Only close and clear selection if successful
            if (result && typeof result === 'object' && 'success' in result && result.success) {
              setSelectedCandidateId(null);
              if (offerMode === ATS_CONSTANTS.OFFER_MODES.BULK) {
                setSelectedCandidateIds(new Set());
              }
            }

            return result;
          }}
        />

        <CloseJobModal
          isOpen={isCloseJobModalOpen}
          onClose={() => setIsCloseJobModalOpen(false)}
          onConfirm={async () => {
            const success = await handleCloseJob();
            if (success) {
              setIsCloseJobModalOpen(false);
            }
          }}
          jobTitle={jobInfo.title}
          acceptedCount={jobInfo.hires}
        />

        <ConfirmationModal
          isOpen={isCancelConfirmOpen}
          onCancel={() => {
            setIsCancelConfirmOpen(false);
            setCandidateToCancelId(null);
          }}
          onConfirm={() => {
            if (activeCandidateToCancel) {
              handleCancelInterviewClick(activeCandidateToCancel);
              setIsCancelConfirmOpen(false);
              setCandidateToCancelId(null);
              // Auto close
              setSelectedCandidateId(null);
            }
          }}
          title="Cancel Interview"
          confirmText="Yes, Cancel"
        >
          Are you sure you want to cancel the interview for {activeCandidateToCancel?.name}?
        </ConfirmationModal>

        <ConfirmationModal
          isOpen={isBulkRejectConfirmOpen}
          onCancel={() => setIsBulkRejectConfirmOpen(false)}
          onConfirm={async () => {
            const selectedCandidatesList = Array.from(selectedCandidateIds).map(
              (id) => findCandidate(id)!.candidate
            );
            for (const candidate of selectedCandidatesList) {
              await handleRejectCandidate(candidate);
            }
            setSelectedCandidateIds(new Set());
            setIsBulkRejectConfirmOpen(false);
          }}
          title="Reject Candidates"
          confirmText="Yes, Reject"
        >
          Are you sure you want to reject {selectedCandidateIds.size} candidates?
        </ConfirmationModal>
      </div>
    </MainLayout>
  );
};

export default ATSPage;
